import React, { useEffect, useState } from 'react';
import { RootState } from 'store/ducks';
import { useDispatch, useSelector } from 'react-redux';
import { addDays, isBefore } from 'date-fns';

import pdfImg from 'img/icon-pdf.svg';
import editImg from 'img/edit.svg';

import { Button } from 'components/Button';
import { Column, Row } from 'components/Layout/Grid';
import Text from 'components/Text';
import Loading from 'components/Loading';

import {
  LegalDocumentClassificationType,
  LegalDocumentComponentProps,
  LegalDocumentType
} from '../interface';
import DownloadService from 'services/download.service';
import { NewLegalDocumentSlider } from '../NewLegalDocumentSlider';
import { LegalDocumentSituationEnum, LegalDocumentTypeEnum } from '../enum';

import { OperationTypes } from 'models/Order';
import DateOutput from 'utils/DateOutput';

import { LegalDocumentStyle } from './styles';

export const LegalDocument: React.FC<LegalDocumentComponentProps> = (props) => {

  const { document } = props;

  const user: any = useSelector((state: RootState) => state.user);
  const customer = useSelector((state: RootState) => state.customer);
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [nameDocument, setNameDocument] = useState("");

  const _downloadService = new DownloadService();

  useEffect(() => {
    if (document.legalDocumentType === LegalDocumentTypeEnum.OTHER) {
      setNameDocument(document.legalDocumentDescription || "Outros");
    } else {
      setNameDocument(LegalDocumentType[document.legalDocumentType].name);
    }
  }, [document]);

  const validateDate = () => {
    const dateValid = addDays(new Date(), 30);
    const compareDate = isBefore(dateValid, new Date(document.periodValidity));

    return (
      <span className={`${!compareDate && 'date-warning'}`}>
        <DateOutput dateStr={document.periodValidity} patternStr="dd/MM/yyyy" />
      </span>
    );
  };

  const downloadLegalDocument = async (legalDocumentId: string) => {
    setLoading(true);

    const customerId = user?.userType === 'employee' ?
      customer?.id :
      user?.selectedCustomerId;

    await _downloadService.DownloadLegalDocument(customerId, OperationTypes.LEGAL_DOCUMENT, legalDocumentId, setLoading);
  };

  const isBoldSituationText = () => {
    const boldSituationList = ["Próximo a vencer", "Vencido", "Em análise"];

    if (boldSituationList.includes(document.legalDocumentSituationType)) {
      return "font-bold";
    }

    return "";
  };

  const disableBtnLegalDocument = () => {
    const { legalDocumentSituationType, legalDocumentType } = document;

    const disabledSituations = [
      LegalDocumentSituationEnum.IN_ANALYSIS,
      LegalDocumentSituationEnum.APPROVED
    ];

    const isSituationDisabled = (situation: any): situation is LegalDocumentSituationEnum =>
      disabledSituations.includes(situation as LegalDocumentSituationEnum);

    if (isSituationDisabled(legalDocumentSituationType) ||
      legalDocumentType === LegalDocumentTypeEnum.OTHER) {
      return true;
    }

    return false;
  };

  return (
    <LegalDocumentStyle>
      <Column className="file-portrait">
        {loading ? (
          <Row className="py-2">
            <Loading size={24} onlySpinner />
          </Row>
        ) : (
          <img
            className="cursor-pointer"
            src={pdfImg}
            alt="icon-download-pdf"
            onClick={() => downloadLegalDocument(document.id)}
            role="presentation"
            title="Clique para fazer o download"
          />
        )}
        <Text className="font-bold mt-3 w-100 text-center" size={11}>
          {nameDocument}
        </Text>
      </Column>
      <Column className="file-options-content">
        <Text size={12}>
          Incluído: {document.creationDate ? <DateOutput dateStr={document.creationDate} /> : '-'}
        </Text>
        <Text size={12}>
          Vigência: {document.periodValidity ? validateDate() : '-'}
        </Text>
        <Text
          className="w-100"
          size={12}
          title={document.userName ?? '-'}
        >
          Por: {document.userName ?? '-'}
        </Text>
        <Text className="d-flex grid-gap-1" size={12}>
          Situação:
          <Text className={isBoldSituationText()} size={12} as="span">{document.legalDocumentSituationType ?? document.legalDocumentSituationType}</Text>
        </Text>
        <Text size={12}>Tipo: {LegalDocumentClassificationType[document.legalDocumentClassificationType].name}</Text>
        <Button
          disabled={disableBtnLegalDocument() || loading}
          className={`${disableBtnLegalDocument() && 'btn-upload-legal-document-disabled'}`}
          onClick={() =>
            dispatch({
              type: 'OPEN_SLIDER',
              data: {
                title: 'Atualizar Documento Legal',
                content: NewLegalDocumentSlider,
                data: {
                  typeDocument: document.legalDocumentType
                }
              },
            })
          }
        >
          <>
            <img src={editImg} alt="" />
            <Text className="ml-2" size={12}>
              Atualizar
            </Text>
          </>
        </Button>
      </Column>
    </LegalDocumentStyle>
  );
};